import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import AttachFileIcon from '@mui/icons-material/AttachFile'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_TOTAL_SIZE = 20 * 1024 * 1024 // 20MB

const ALLOWED_FILE_TYPES = [
  'application/pdf'
]

const ALLOWED_EXTENSIONS = ['.pdf']

const FileUploadSection = ({ files, onFilesChange }) => {
  const { t } = useTranslation()
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState('')

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const validateFiles = useCallback((newFiles) => {
    const fileArray = Array.from(newFiles)

    const invalidFile = fileArray.find(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase()
      return !ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension)
    })
    if (invalidFile) {
      setError(t('mailTemplate.campaigns.modal.fileTypeError', { fileName: invalidFile.name }))
      return null
    }

    const oversizedFile = fileArray.find(file => file.size > MAX_FILE_SIZE)
    if (oversizedFile) {
      setError(t('mailTemplate.campaigns.modal.fileSizeError', { maxSize: '10MB' }))
      return null
    }

    const currentTotalSize = files.reduce((acc, file) => acc + file.size, 0)
    const newTotalSize = fileArray.reduce((acc, file) => acc + file.size, 0)

    if (currentTotalSize + newTotalSize > MAX_TOTAL_SIZE) {
      setError(t('mailTemplate.campaigns.modal.totalSizeError', { maxSize: '20MB' }))
      return null
    }

    setError('')
    return fileArray
  }, [files, t])

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const validatedFiles = validateFiles(e.dataTransfer.files)
      if (validatedFiles) {
        onFilesChange([...files, ...validatedFiles])
      }
    }
  }, [files, onFilesChange, validateFiles])

  const handleFileInput = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const validatedFiles = validateFiles(e.target.files)
      if (validatedFiles) {
        onFilesChange([...files, ...validatedFiles])
      }
    }
    e.target.value = ''
  }, [files, onFilesChange, validateFiles])

  const handleRemoveFile = useCallback((index) => {
    const newFiles = files.filter((_, i) => i !== index)
    onFilesChange(newFiles)
    setError('')
  }, [files, onFilesChange])

  const totalSize = files.reduce((acc, file) => acc + file.size, 0)

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: dragActive ? '2px dashed #0fc6b9' : '2px dashed #e0e0e0',
          backgroundColor: dragActive ? '#f0fffe' : '#fafafa',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: '#0fc6b9',
            backgroundColor: '#f0fffe'
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />

        <CloudUploadIcon sx={{ fontSize: 48, color: '#0fc6b9', mb: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t('mailTemplate.campaigns.modal.uploadFiles')}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          {t('mailTemplate.campaigns.modal.dragAndDrop')}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          {t('mailTemplate.campaigns.modal.maxFileSize')}: 10MB | {t('mailTemplate.campaigns.modal.maxTotalSize')}: 20MB
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {t('mailTemplate.campaigns.modal.supportedFormats')}: PDF
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('mailTemplate.campaigns.modal.attachedFiles')} ({files.length})
            </Typography>
            <Chip
              label={`${formatFileSize(totalSize)} / 20MB`}
              size="small"
              color={totalSize > MAX_TOTAL_SIZE ? 'error' : 'primary'}
              variant="outlined"
            />
          </Box>

          <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
            {files.map((file, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  '&:last-child': { mb: 0 }
                }}
              >
                <AttachFileIcon sx={{ mr: 2, color: 'primary.main' }} />
                <ListItemText
                  primary={file.name}
                  secondary={formatFileSize(file.size)}
                  primaryTypographyProps={{
                    sx: { fontWeight: 500 }
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveFile(index)}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.lighter'
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  )
}

export default FileUploadSection
